import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { createFeedback } from '../../api/runs';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import ErrorAlert from '../../components/ErrorAlert';

/**
 * Post-run feedback form.
 *
 * Route: `/runs/:runId/participant/feedback` (and `/runs/:runId/feedback`).
 * Anonymous on the backend — the server must NOT link feedback to the
 * authenticated user id. We only send { rating, comment? }.
 *
 * 5-star touch-friendly rating + optional free-text comment. After
 * submit: thank-you card with a single "Back" button.
 */
const FeedbackForm: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const { user } = useAuth();
  const { t } = useTranslation('runs');
  const navigate = useNavigate();

  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const orgSlug = user?.organization_slug;
  const canSubmit = !!rating && rating > 0 && !submitting && !!orgSlug && !!runId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await createFeedback(orgSlug!, runId!, {
        rating: rating!,
        comment: comment.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h2" component="h1" gutterBottom>
            {t('participant.feedback.title')}
          </Typography>

          {done ? (
            <Stack spacing={2} sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="h5" component="p">
                {t('participant.feedback.thankYou')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('participant.feedback.thankYouSubtitle')}
              </Typography>
              <Box>
                {/* The standing, not the stations. Feedback is asked once the
                    run has ended, so there is nothing left to do at a station —
                    what a team still wants after finishing is where it came out.
                    Scoring usually finishes later, so this is also the view they
                    come back to when the coordination is done. */}
                <Button
                  variant="contained"
                  onClick={() => navigate(`/runs/${runId}/participant/team/leaderboard`)}
                  sx={{ minWidth: 160 }}
                >
                  {t('participant.nav.leaderboard')}
                </Button>
              </Box>
            </Stack>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t('participant.feedback.intro')}
              </Typography>
              {error && (
                <Box sx={{ mb: 2 }}>
                  <ErrorAlert message={error} />
                </Box>
              )}
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    component="label"
                    htmlFor="feedback-rating"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    {t('participant.feedback.rating')}
                  </Typography>
                  <Rating
                    id="feedback-rating"
                    value={rating}
                    onChange={(_, next) => setRating(next)}
                    size="large"
                    sx={{ fontSize: '2.5rem' }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    component="label"
                    htmlFor="feedback-comment"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    {t('participant.feedback.commentLabel')}
                  </Typography>
                  <TextField
                    id="feedback-comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                    placeholder={t('participant.feedback.comment')}
                    slotProps={{ htmlInput: { maxLength: 2000 } }}
                  />
                </Box>

                <Box>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!canSubmit}
                    sx={{ minWidth: 200 }}
                  >
                    {t('participant.feedback.submit')}
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default FeedbackForm;
